/**
 * AI 응답용 경량 마크다운 렌더러 (외부 의존성 없음).
 *
 * 백엔드 AI 응답이 보내는 마크다운 부분집합만 지원:
 *   ### 제목 / **굵게** / `인라인코드` / | 표 | (구분행 |---|, ---: 우측정렬, :---: 가운데)
 *   ```sql 코드블록``` / --- 구분선 / - 목록
 *
 * 줄 단위로 파싱해 React 엘리먼트로 조립한다 — dangerouslySetInnerHTML 미사용(XSS 안전).
 * 루트에 whiteSpace: normal 을 줘서 말풍선의 pre-wrap 을 무효화한다.
 */
import React from "react";
import { C } from "@shared/libs/designTokens";

const MONO = "'D2Coding','Consolas',monospace";

const st = {
  root: {
    whiteSpace: "normal",
    wordBreak: "break-word",
  } as React.CSSProperties,
  heading: {
    fontWeight: 800,
    fontSize: 12.5,
    color: C.navy,
    margin: "8px 0 4px",
  } as React.CSSProperties,
  hr: {
    border: 0,
    borderTop: `1px solid ${C.border}`,
    margin: "8px 0",
  } as React.CSSProperties,
  ul: { margin: "4px 0", paddingLeft: 18 } as React.CSSProperties,
  li: { margin: "1px 0" } as React.CSSProperties,
  para: { margin: "1px 0" } as React.CSSProperties,
  blank: { height: 6 } as React.CSSProperties,
  codeInline: {
    fontFamily: MONO,
    fontSize: "0.92em",
    color: C.navy,
    background: "rgba(11,12,105,0.06)",
    border: `1px solid ${C.borderSoft}`,
    borderRadius: 4,
    padding: "0 4px",
  } as React.CSSProperties,
  codeBlock: {
    margin: "6px 0",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "8px 10px",
    fontFamily: MONO,
    fontSize: 11,
    lineHeight: 1.55,
    color: C.text,
    overflowX: "auto",
    whiteSpace: "pre",
  } as React.CSSProperties,
  // 말풍선 maxWidth(88%)를 넘칠 수 있어 표는 가로 스크롤 래퍼로 감싼다
  tableWrap: { overflowX: "auto", margin: "6px 0" } as React.CSSProperties,
  table: {
    borderCollapse: "collapse",
    background: "#fff",
    fontSize: 11.5,
    lineHeight: 1.45,
  } as React.CSSProperties,
  th: {
    border: `1px solid ${C.border}`,
    background: "#f4f6fa",
    padding: "4px 9px",
    fontWeight: 700,
    color: C.text,
    whiteSpace: "nowrap",
  } as React.CSSProperties,
  td: {
    border: `1px solid ${C.border}`,
    padding: "4px 9px",
    color: C.text,
    whiteSpace: "nowrap",
  } as React.CSSProperties,
};

/** 인라인 마크다운: **굵게** / `코드` (굵게 안의 `코드`도 지원) */
function parseInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`)/g)
    .filter((part) => part !== "")
    .map((part, i) => {
      const key = `${keyPrefix}-${i}`;
      if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
        return <strong key={key}>{parseInline(part.slice(2, -2), key)}</strong>;
      }
      if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
        return (
          <code key={key} style={st.codeInline}>
            {part.slice(1, -1)}
          </code>
        );
      }
      return <React.Fragment key={key}>{part}</React.Fragment>;
    });
}

/** | a | b | 행 → 셀 배열 */
function splitRow(row: string): string[] {
  return row
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

/** 구분행(|---|:---:|---:|) 여부 */
function isSeparatorRow(cells: string[]): boolean {
  return cells.length > 0 && cells.every((c) => /^:?-{3,}:?$/.test(c));
}

/** 구분행 셀 → 정렬 (---: 우측, :---: 가운데, 그 외 좌측) */
function cellAlign(sep: string | undefined): "left" | "center" | "right" {
  if (!sep) return "left";
  const left = sep.startsWith(":");
  const right = sep.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  return "left";
}

function renderTable(tableLines: string[], key: string): React.ReactNode {
  const rows = tableLines.map(splitRow);
  const hasHeader = rows.length >= 2 && isSeparatorRow(rows[1]);
  const aligns = hasHeader ? rows[1].map(cellAlign) : [];
  const header = hasHeader ? rows[0] : null;
  const body = hasHeader ? rows.slice(2) : rows;

  return (
    <div key={key} style={st.tableWrap}>
      <table style={st.table}>
        {header && (
          <thead>
            <tr>
              {header.map((cell, ci) => (
                <th key={ci} style={{ ...st.th, textAlign: aligns[ci] ?? "left" }}>
                  {parseInline(cell, `${key}-h${ci}`)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((cells, ri) => (
            <tr key={ri}>
              {cells.map((cell, ci) => (
                <td key={ci} style={{ ...st.td, textAlign: aligns[ci] ?? "left" }}>
                  {parseInline(cell, `${key}-r${ri}c${ci}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function parseBlocks(text: string): React.ReactNode[] {
  const lines = text.split(/\r?\n/);
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    const key = `blk${k++}`;

    // ```lang 코드블록 ``` — 닫는 펜스가 없으면 끝까지
    if (trimmed.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        buf.push(lines[i]);
        i++;
      }
      i++; // 닫는 ``` 건너뜀
      out.push(
        <pre key={key} className="snd-scroll" style={st.codeBlock}>
          {buf.join("\n")}
        </pre>
      );
      continue;
    }

    // | 표 |
    if (trimmed.startsWith("|")) {
      const tbl: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tbl.push(lines[i].trim());
        i++;
      }
      out.push(renderTable(tbl, key));
      continue;
    }

    // --- 구분선
    if (/^-{3,}$/.test(trimmed)) {
      out.push(<hr key={key} style={st.hr} />);
      i++;
      continue;
    }

    // #~#### 제목
    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      out.push(
        <div key={key} style={st.heading}>
          {parseInline(heading[2], key)}
        </div>
      );
      i++;
      continue;
    }

    // - 목록
    if (trimmed.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      out.push(
        <ul key={key} style={st.ul}>
          {items.map((item, li) => (
            <li key={li} style={st.li}>
              {parseInline(item, `${key}-li${li}`)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // 빈 줄 → 문단 간격
    if (trimmed === "") {
      out.push(<div key={key} style={st.blank} />);
      i++;
      continue;
    }

    // 일반 문단 (한 줄 = 한 블록, pre-wrap 없이도 줄바꿈 유지)
    out.push(
      <div key={key} style={st.para}>
        {parseInline(trimmed, key)}
      </div>
    );
    i++;
  }
  return out;
}

export default function AiMarkdown({ text }: { text: string }) {
  return <div style={st.root}>{parseBlocks(text ?? "")}</div>;
}
